import React, { ReactNode, useEffect, useState } from "react";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Fields, FieldButton, AlertModal } from "components/voc";
import styled from "styled-components";
import { useModalState } from "hooks";

const FieldSetHookFormStyled = styled.fieldset`
  margin-top: 0.3rem;

  .btn_default {
    display: flex;
    width: 23rem;
    margin: 0 auto;
  }
`;

type formGroupElementTypes = React.InputHTMLAttributes<HTMLInputElement> & {
  fieldLabelText?: string;
  mandatory?: boolean;
  noti?: string;
  valid?: yup.Schema;
  labelText?: string;
  buttonClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type FormGroupTypes = {
  formType: "radio" | "checkbox";
  form: formGroupElementTypes[];
};

type FieldSetInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  fieldLabelText?: string;
  mandatory?: boolean;
  noti?: string;
  valid?: yup.Schema;
  formGroup?: FormGroupTypes;
  hiddenType?: "time" | "";
  hiddenFunction?: () => void;
  cascadeField?: string;
  buttonClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type FieldInputProps = React.InputHTMLAttributes<HTMLInputElement>;

type FieldSetHookFormProps = {
  legend: string;
  showSubmit?: boolean;
  onSubmit?: SubmitHandler<any>;
  children?: ReactNode;
  inputs: FieldSetInputProps[];
} & Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit">;

const FieldSetHookForm = ({ legend, showSubmit, onSubmit, children, inputs, ...props }: FieldSetHookFormProps) => {
  const validField: any = {};
  inputs.map((el) => {
    if (el.valid) {
      validField[el.name!] = el.valid;
    }
  });

  const validationSchema = yup.object().shape({
    ...validField,
  });

  const methods = useForm({
    ...(validationSchema && { resolver: yupResolver(validationSchema) }),
    shouldFocusError: false,
  });

  const [alertMsg, setAlertMsg] = useState({
    title: (
      <>
        필수로 입력해야 하는 항목이
        <br />
        입력되지 않았습니다.
      </>
    ),
    desc: (
      <>
        문의 접수를 위해, <br />
        다시 한번 확인 부탁 드립니다
      </>
    ),
  });

  const modalState = useModalState();

  useEffect(() => {
    if (Object.keys(methods.formState.errors).length) {
      // console.log(Object.keys(methods.formState.errors).length, methods.formState.errors);
      // console.log(methods.formState.errors);

      if (Object.keys(methods.formState.errors).includes("email") && methods.formState.errors.email?.type === "matches") {
        setAlertMsg((prev) => ({ ...prev, title: <>{methods.formState.errors.email?.message}</> }));
      } else if (Object.keys(methods.formState.errors).includes("privacy") && methods.formState.errors.privacy?.type === "nullable") {
        setAlertMsg((prev) => ({ ...prev, title: <>{methods.formState.errors.privacy?.message}</> }));
      } else {
        setAlertMsg((prev) => ({
          ...prev,
          title: (
            <>
              필수로 입력해야 하는 항목이
              <br />
              입력되지 않았습니다.
            </>
          ),
        }));
      }
      modalState.show();
    }
  }, [methods.formState.errors]);

  return (
    <>
      <FieldSetHookFormStyled>
        <legend>{legend}</legend>
        <FormProvider {...methods}>
          <form {...(onSubmit && { onSubmit: methods.handleSubmit(onSubmit) })} {...props}>
            <Fields inputs={inputs} />
            {showSubmit && onSubmit! && (
              <FieldButton type="submit" className="btn_default -lg" disabled={methods.formState.isSubmitting}>
                접수하기
              </FieldButton>
            )}
          </form>
        </FormProvider>
        <AlertModal {...alertMsg} show={modalState.showState} onClose={modalState.show} />
      </FieldSetHookFormStyled>
    </>
  );
};

export default FieldSetHookForm;
export type { FieldSetInputProps, FieldInputProps };
